import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { SVGMap } from "react-svg-map";
import chroma from "chroma-js";

import { colors } from "../../theme";
import GlobalTooltip from "../modal/Tooltip";

const Container = styled.div`
  svg {
    stroke: white;
    fill: ${chroma(colors.borderColor).brighten(0.6)};
  }

  path {
    transition: 0.4s;
  }
`;

const Tooltip = styled(GlobalTooltip)`
  font-size: 1.6rem;
  line-height: 1.15;
  padding: 14px 14px 8px;

  h1 {
    color: black;
    font-size: 1.2rem;
    font-weight: 600;
    line-height: 1.15rem;
    margin: 0 0 5px;
    text-transform: uppercase;
  }
`;

const TooltipContent = styled.p`
  margin: 0 !important;

  span {
    color: ${(props) => props.color || "inherit"};
    font-weight: 500;
  }
`;

function DataMap({ map, containerRef, getTooltipContent, forceTooltipUpdate }) {
  const [tooltipContent, setTooltipContent] = useState({});
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  useEffect(() => {
    if (isTooltipVisible) {
      setTooltipContent((tooltipContent) => {
        return {
          ...tooltipContent,
          ...getTooltipContent(tooltipContent.id),
        };
      });
    }
  }, [forceTooltipUpdate, getTooltipContent, isTooltipVisible]);

  function getTooltipVirtualElement(x, y) {
    return {
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        top: y,
        right: x,
        bottom: y,
        left: x,
      }),
    };
  }

  function handleLocationMouseOver(e) {
    if (getTooltipContent) {
      const { clientX, clientY } = e;
      setIsTooltipVisible(true);
      setTooltipContent({
        ...getTooltipContent(e.target.id),
        id: e.target.id,
        virtualElement: getTooltipVirtualElement(clientX, clientY),
      });
    }
  }

  function handleLocationMouseMove(e) {
    if (isTooltipVisible) {
      const { clientX, clientY } = e;
      setTooltipContent((tooltipContent) => ({
        ...tooltipContent,
        virtualElement: getTooltipVirtualElement(clientX, clientY),
      }));
    }
  }

  function handleLocationMouseOut() {
    if (isTooltipVisible) {
      setIsTooltipVisible(false);
    }
  }

  return (
    <Container ref={containerRef}>
      <SVGMap
        onLocationMouseOver={handleLocationMouseOver}
        onLocationMouseMove={handleLocationMouseMove}
        onLocationMouseOut={handleLocationMouseOut}
        map={map}
      />
      {isTooltipVisible && (
        <Tooltip refElement={tooltipContent.virtualElement}>
          <h1>{tooltipContent.title}</h1>
          {tooltipContent.mainContent && (
            <TooltipContent color={tooltipContent.color}>
              {tooltipContent.mainContent}
            </TooltipContent>
          )}
          {tooltipContent.additionalContent}
        </Tooltip>
      )}
    </Container>
  );
}

DataMap.propTypes = {
  map: PropTypes.object.isRequired,
  containerRef: PropTypes.shape({ current: PropTypes.any }),
  getTooltipContent: PropTypes.func,
  forceTooltipUpdate: PropTypes.any,
};

export default DataMap;
